import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { View } from "react-native";
import { Input, Spacer, VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import {
  setFiltersPayload,
  useValueFromPayloadRange,
} from "@redux/slices/searchFilters";
import { useDispatch } from "react-redux";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import { VDBIcons } from "@utils/VDBIcons";
import { ProductCategory } from "@utils/constants";

type RangeSliderSectionProps = {
  minVal: number;
  maxVal: number;
  sectionTitle: string;
  payloadName?: Array<string>;
  sectionName?: string;
  selectedStyle: SelectedItemTheme;
  selectedProduct: ProductCategory;
  isCollapsible?: boolean;
};

const RangeSliderSection = ({
  minVal = 0,
  maxVal = 0,
  sectionTitle = "",
  payloadName = [],
  sectionName = "",
  selectedStyle,
  isCollapsible = false,
}: RangeSliderSectionProps) => {
  const isTouched = useRef(false);
  const typography = useTypographyStore();
  const theme = useTheme();
  const value = useValueFromPayloadRange(sectionName, payloadName);
  const [values, setValues] = useState([
    +(value?.[0] || minVal),
    +(value?.[1] || maxVal),
  ]);
  const dispatch = useDispatch();

  const handleMinInputChange = useCallback(
    (newValue: string) => {
      isTouched.current = true;
      const newMinValue = parseInt(newValue, 10);
      if (!isNaN(newMinValue) && newMinValue <= values[1]) {
        setValues([newMinValue, values[1]]);
      }
    },
    [values]
  );

  const handleMaxInputChange = useCallback(
    (newValue: string) => {
      isTouched.current = true;
      const newMaxValue = parseInt(newValue, 10);
      if (!isNaN(newMaxValue) && newMaxValue >= values[0]) {
        setValues([values[0], newMaxValue]);
      }
    },
    [values]
  );

  useEffect(() => {
    if (!isTouched.current) return;

    const payload: any = {};
    if (payloadName) {
      if (values[0] !== null) {
        payload[payloadName[0]] = values[0].toString();
      } else payload[payloadName[0]] = "";
      if (values[1] !== null) {
        payload[payloadName[1]] = values[1].toString();
      } else payload[payloadName[1]] = "";
    }
    dispatch(setFiltersPayload({ sectionName, data: payload }));
  }, [values]);

  return (
    <ParentContainer>
      {sectionTitle && !isCollapsible && (
        <>
          <VDBText isBold type={typography["H3-M"]} color={theme["core-05"]}>
            {sectionTitle}
          </VDBText>
          <Spacer size={SpaceType.Extra} />
        </>
      )}
      <CompareView>
        <View style={{ flex: 1 }}>
          <Input
            inputLabel={"Min"}
            value={values[0].toString()}
            onChangeText={handleMinInputChange}
            selectedStyle={selectedStyle}
          />
        </View>
        <VDBIcon
          name={VDBIcons.ARROW_RIGHT}
          size={24}
          color={theme["core-04"]}
          variant={IconsVariant.VDBCustom}
        />
        <View style={{ flex: 1 }}>
          <Input
            inputLabel={"Max"}
            value={values[1].toString()}
            onChangeText={handleMaxInputChange}
            selectedStyle={selectedStyle}
          />
        </View>
      </CompareView>
      <SliderContainer />
    </ParentContainer>
  );
};

const CompareView = styled.View`
  gap: ${SpaceType.Basic}px;
  flex-direction: row;
  align-items: center;
`;
const SliderContainer = styled.View`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const ParentContainer = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

export default memo(RangeSliderSection);
